import { formatCost } from './formatters';
type CostText = (key: string, fallback: string, values?: Record<string, unknown>) => string;
const fallbackText: CostText = (_key, text, values = {}) => text.replace(/%{(\w+)}/g, (_, key) => String(values[key] ?? ''));


export interface AICostCoverage {
  status: 'no_data' | 'provider_reported' | 'estimated' | 'partial' | 'historical';
  providerCostUSD: number;
  estimatedCostUSD: number;
  unclassifiedCostUSD: number;
  legacyCostUSD?: number;
  legacyCalls?: number;
  legacyTokens?: number;
  providerCalls: number;
  estimatedCalls: number;
  unknownCostCalls: number;
  missingUsageCalls: number;
}

export const AI_COST_QUERY_OPTIONS = {
  refetchInterval: 15_000,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
  staleTime: 5_000,
};

export interface AICostCall {
  id: string;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  timestamp: string;
  model: string;
  provider: string | null;
  serviceName: string | null;
  operationId: string | null;
  providerRequestId: string | null;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cachedTokens: number | null;
  cacheWriteTokens: number | null;
  reasoningTokens: number | null;
  billingUnit: string | null;
  billableUnits: number | null;
  costUSD: number;
  costSource: 'provider' | 'catalog_estimate' | 'unknown' | 'legacy';
  usageReported: boolean;
}

export interface AICostCallsResult {
  data: AICostCall[];
  total: number;
  summary: {
    recordedCostUSD: number;
    costCoverage: AICostCoverage;
    reportedInputTokens: number;
    reportedOutputTokens: number;
    reportedTotalTokens: number;
    unattributedCalls: number;
  };
  generatedAt: string;
}

export interface AICostCallFilters {
  from?: string;
  to?: string;
  attribution?: 'all' | 'unattributed';
  userId?: string;
  operationId?: string;
  serviceName?: string;
  provider?: string;
}

/** Null means not supplied by the provider; zero is a reported value. */
export function presentUsage(value: number | null, reported = true): string {
  return reported && value !== null ? value.toLocaleString() : 'Unknown';
}

export function presentToolUsage(units: number | null, unit: string | null): string {
  return units !== null && unit ? `${units.toLocaleString()} ${unit}` : 'Not reported';
}

/** Legacy and incomplete totals must not look like an exact provider bill. */
export function presentRecordedCost(total: number, coverage?: AICostCoverage, t: CostText = fallbackText) {
  if (!coverage) return { value: `Unverified ${formatCost(total)}`,
    detail: 'Cost provenance unavailable; this amount may include estimates and omit unknown costs.' };
  if (coverage.status === 'no_data') return { value: 'No data', detail: 'No recorded AI calls.' };
  const qualifier = coverage.status === 'partial' ? 'Partial ' : coverage.status === 'estimated' ? 'Est. ' : coverage.status === 'historical' ? `${t('historicalMix', 'Historical mix')} ` : '';
  return {
    value: coverage.unknownCostCalls && !coverage.providerCalls && !coverage.estimatedCalls && !coverage.legacyCalls
      ? 'Unknown' : `${qualifier}${formatCost(total)}`,
    detail: [
      coverage.providerCalls ? `Provider reported: ${formatCost(coverage.providerCostUSD)}` : 'No provider-reported cost',
      ...(coverage.estimatedCalls ? [`Catalog estimates: ${formatCost(coverage.estimatedCostUSD)}`] : []),
      ...(coverage.legacyCalls ? [
        t('historicalCostDetail', 'Historical records: %{cost} across %{count} calls; original cost provenance was not recorded', { cost: formatCost(coverage.legacyCostUSD ?? 0), count: coverage.legacyCalls }),
        t('historicalTokens', '%{count} historical tokens retained separately', { count: (coverage.legacyTokens ?? 0).toLocaleString() }),
      ] : []),
      ...(coverage.unknownCostCalls ? [
        `Unclassified recorded amount: ${formatCost(coverage.unclassifiedCostUSD)}`,
        `${coverage.unknownCostCalls} calls with unknown cost provenance; total spend is unknown`,
      ] : []),
      ...(coverage.missingUsageCalls ? [`${coverage.missingUsageCalls} calls with missing token usage`] : []),
    ].join(' · '),
  };
}

export function presentCostLog(cost: number, source?: 'provider' | 'catalog_estimate' | 'unknown' | 'legacy' | null, t: CostText = fallbackText) {
  if (source === 'legacy') return { value: t('historicalCost', 'Historical %{cost}', { cost: formatCost(cost) }), detail: t('historicalCostSource', 'Original amount; cost provenance not recorded') };
  if (source === 'provider') return { value: formatCost(cost), detail: 'Provider reported' };
  if (source === 'catalog_estimate') return { value: `Est. ${formatCost(cost)}`, detail: 'Catalog estimate' };
  return { value: 'Unknown', detail: cost ? `Unclassified recorded amount: ${formatCost(cost)}` : 'Cost was not reported' };
}
